/* Copyright (c) 2020-present CloudIO Inc */

import { ComponentSample, Item } from 'componentregistry';
import type { AsyncSelectColumn } from './AsyncSelectColumn';

const page: {
  root: Item<'Page'>;
  flexPanel: Item<'FlexPanel'>;
  flexItem: Item<'FlexItem'>;
  testAliasLocalDataStore: Item<'LocalDataStore'>;
  asyncSelectLookupDataStore: Item<'LocalDataStore'>;
  testAliasDataStoreReference: Item<'DataStoreReference'>;
  dataGrid: Item<'DataGrid'>;
  idNumberColumn: Item<'NumberColumn'>;
  asyncSelectColumn: Item<'AsyncSelectColumn'>;
} = {
  root: {
    children: [
      'testAliasLocalDataStore',
      'asyncSelectLookupDataStore',
      'flexPanel',
    ],
    itemId: 'root',
    parent: 'root',
    props: {
      padding: { all: 0 },
      windowTitle: { type: 'text', value: 'Async Select Column Example' },
    },
    type: 'Page',
  },

  flexPanel: {
    children: ['flexItem'],
    itemId: 'flexPanel',
    parent: 'root',
    props: {},
    type: 'FlexPanel',
  },

  flexItem: {
    children: ['testAliasDataStoreReference'],
    itemId: 'flexItem',
    parent: 'flexPanel',
    props: {},
    type: 'FlexItem',
  },

  testAliasLocalDataStore: {
    children: [],
    itemId: 'testAliasLocalDataStore',
    parent: 'root',
    props: {
      alias: 'testAlias',
      dataFormat: 'json',
      dataSource: 'test',
      jsonData: `[
  { "id": 1, "countryCode": "IN" },
  { "id": 2, "countryCode": "US" },
  { "id": 3, "countryCode": "BR" },
  { "id": 4, "countryCode": "IT" },
  { "id": 5, "countryCode": "FR" }
  ]`,
    },
    type: 'LocalDataStore',
  },

  asyncSelectLookupDataStore: {
    children: [],
    itemId: 'asyncSelectLookupDataStore',
    parent: 'root',
    props: {
      alias: 'asyncSelectLookupAlias',
      dataFormat: 'json',
      dataSource: 'countryLookup',
      jsonData: `[
  { "code": "IN", "label": "India" },
  { "code": "US", "label": "USA" },
  { "code": "FR", "label": "France" },
  { "code": "CN", "label": "China" },
  { "code": "CA", "label": "Canada" },
  { "code": "IT", "label": "Italy" },
  { "code": "BR", "label": "Brazil" }
]`,
    },
    type: 'LocalDataStore',
  },

  testAliasDataStoreReference: {
    children: ['dataGrid'],
    itemId: 'testAliasDataStoreReference',
    parent: 'flexItem',
    props: { alias: 'testAlias' },
    type: 'DataStoreReference',
  },

  dataGrid: {
    children: ['idNumberColumn', 'asyncSelectColumn'],
    itemId: 'dataGrid',
    parent: 'testAliasDataStoreReference',
    props: {
      expandLastColumn: false,
      showBorder: true,
      updatable: { type: 'boolean', value: true },
      insertable: { type: 'boolean', value: true },
    },
    type: 'DataGrid',
  },

  idNumberColumn: {
    children: [],
    itemId: 'idNumberColumn',
    parent: 'dataGrid',
    props: {
      allowDecimal: false,
      label: { type: 'text', value: 'ID' },
      viewAttribute: 'id',
      width: 80,
    },
    type: 'NumberColumn',
  },

  asyncSelectColumn: {
    children: [],
    itemId: 'asyncSelectColumn',
    parent: 'dataGrid',
    props: {
      alignment: 'center',
      label: { type: 'text', value: 'Country (Async)' },
      viewAttribute: 'countryCode',
      displayAttribute: 'label',
      comboDatasourceAlias: 'asyncSelectLookupAlias',
      valueAttribute: 'code',
      labelAttribute: 'label',
      minFilterChars: 1,
      placeholder: 'Start typing country name...',
      selectOptionOnTab: true,
      selectOptionOnBlur: false,
      serverValidateOnChange: false,
      unknownValueMessage: { type: 'expression', value: "'Invalid country!'" },
      width: 240,
      updatable: { type: 'boolean', value: true },
      insertable: { type: 'boolean', value: true },
      filterable: false,
      sortable: true,
    } as Required<AsyncSelectColumn>,
    type: 'AsyncSelectColumn',
  },
};

export const helpRenders: ComponentSample<'AsyncSelectColumn'>[] = [
  {
    title: 'Async Select Column Example',
    page,
    exampleId: 'eg-01GK8QWM9KTAE6TN0ME1F8WQASYNC',
    clearParentChildren: false,
    height: 420,
    visibleProps: [
      'label',
      'alignment',
      'viewAttribute',
      'displayAttribute',
      'comboDatasourceAlias',
      'valueAttribute',
      'labelAttribute',
      'minFilterChars',
      'placeholder',
      'selectOptionOnTab',
      'selectOptionOnBlur',
      'serverValidateOnChange',
      'unknownValueMessage',
      'width',
      'pinLeft',
      'pinRight',
      'sortable',
      'filterable',
      'updatable',
      'insertable',
    ],
    item: {
      children: [],
      itemId: 'asyncSelectColumn',
      parent: 'dataGrid',
      type: 'AsyncSelectColumn',
      props: {
        updatable: { type: 'boolean', value: true },
        insertable: { type: 'boolean', value: true },
        alignment: 'center',
        label: { type: 'text', value: 'Country (Async)' },
        viewAttribute: 'countryCode',
        displayAttribute: 'countryCode',
        comboDatasourceAlias: 'asyncSelectLookupAlias',
        valueAttribute: 'code',
        labelAttribute: 'label',
        minFilterChars: 0,
        placeholder: 'Start typing country name...',
        selectOptionOnTab: true,
        selectOptionOnBlur: false,
        serverValidateOnChange: false,
        unknownValueMessage: {
          type: 'expression',
          value: "'Invalid country!'",
        },
        width: 240,
        filterable: false,
      } as Required<AsyncSelectColumn>,
    },
  },
];
